import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './MedicalTravellerFormLanguage.css';
import header from './assets/images/IslandHospital nice.png';
import leafSvg from './assets/leaf.svg';
import logo from './assets/images/logo.png';

const SURVEY_ID = '68c0a1b2ece92761170b9601';
const FORM_PATH = '/f/TWVkaWNhbFRyYXZlbGxlckZvcm0=';

/** Survey open window in Malaysia time (Asia/Kuala_Lumpur, UTC+8). */
const SURVEY_START_MYT = new Date('2026-09-01T00:00:00+08:00');
const SURVEY_END_MYT = new Date('2026-10-31T23:59:59.999+08:00');
const isMedicalTravellerSurveyAvailable = (now = new Date()) =>
    now >= SURVEY_START_MYT && now <= SURVEY_END_MYT;

const API_SERVER = (() => {
    const configured = (process.env.REACT_APP_AI_FORM_SERVER || '').replace(/\/$/, '');
    if (configured && /islandhospital\.com/i.test(configured)) {
        return configured;
    }
    if (typeof window !== 'undefined' && window.location?.hostname) {
        return `${window.location.protocol}//${window.location.hostname}:4454`;
    }
    return configured || 'http://localhost:4454';
})();

function MedicalTravellerFormLanguage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const agentIdParam = searchParams.get('AgentId') || searchParams.get('agentId') || '';

    const [agentCheck, setAgentCheck] = useState({ status: 'loading', agentId: null });

    useEffect(() => {
        if (!isMedicalTravellerSurveyAvailable()) {
            return;
        }

        const validateAgent = async () => {
            try {
                const response = await fetch(`${API_SERVER}/api/validateMedicalTravellerAgent`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        agentId: agentIdParam || null,
                        survey_id: SURVEY_ID,
                    }),
                });
                const data = await response.json();

                if (!data.success) {
                    window.alert('Failed to validate agent. Please try again.');
                    setAgentCheck({ status: 'error', agentId: null });
                    return;
                }

                if (!data.valid) {
                    window.alert('No such agent');
                    setAgentCheck({ status: 'invalid', agentId: null });
                    return;
                }

                setAgentCheck({
                    status: 'ready',
                    agentId: data.agent?.agentId || 'MedicalTraveller',
                    agentName: data.agent?.agentName || '',
                    usedDefault: Boolean(data.usedDefault),
                });
            } catch (error) {
                console.log('Error validating agent:', error);
                window.alert('Failed to validate agent. Please try again.');
                setAgentCheck({ status: 'error', agentId: null });
            }
        };

        validateAgent();
    }, [agentIdParam]);

    const handleLanguageSelect = (language) => {
        if (agentCheck.status !== 'ready') return;

        const query = agentIdParam
            ? `?AgentId=${encodeURIComponent(agentIdParam)}`
            : '';

        navigate(`${FORM_PATH}${query}`, {
            state: {
                language,
                agentId: agentCheck.agentId,
            },
        });
    };

    const renderHero = () => (
        <div className="mt-lang-hero">
            <img
                src={leafSvg}
                alt=""
                className="mt-lang-hero-leaf mt-lang-hero-leaf--right"
                aria-hidden="true"
            />
            <img
                src={leafSvg}
                alt=""
                className="mt-lang-hero-leaf mt-lang-hero-leaf--left"
                aria-hidden="true"
            />
            <div className="mt-lang-hero-kicker">Island Hospital · 2026</div>
            <div className="mt-lang-hero-icon">
                <img src={logo} alt="Island Hospital" className="mt-lang-hero-icon-img" />
            </div>
            <h1 className="mt-lang-hero-title">
                Medical Traveller Accommodation &amp; Travel Survey
            </h1>
            <p className="mt-lang-hero-subtitle">
                Survei Akomodasi &amp; Perjalanan Wisata Medis
            </p>
  
        </div>
    );

    if (!isMedicalTravellerSurveyAvailable()) {
        return (
            <div className="mt-lang-page">
                {renderHero()}
                <div className="mt-lang-card-tray">
                    <div className="mt-lang-card">
                        <h2 className="mt-lang-error-title">
                            This survey is unavailable / Survei ini tidak tersedia
                        </h2>
                        <p className="mt-lang-error-text">
                            This survey is only available until 31 October 2026 (Malaysia time).
                            <br />
                            Survei ini hanya tersedia hingga 31 Oktober 2026 (waktu Malaysia).
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (agentCheck.status === 'invalid' || agentCheck.status === 'error') {
        return (
            <div className="mt-lang-page">
                {renderHero()}
                <div className="mt-lang-card-tray">
                    <div className="mt-lang-card">
                        <h2 className="mt-lang-error-title">
                            {agentCheck.status === 'invalid'
                                ? 'No such agent / Agen tidak ditemukan'
                                : 'Unable to validate agent / Gagal memvalidasi agen'}
                        </h2>
                        <p className="mt-lang-error-text">
                            Please contact your agent for a valid survey link.
                            <br />
                            Silakan hubungi agen Anda untuk mendapatkan tautan survei yang valid.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="mt-lang-page">
            {renderHero()}

            <div className="mt-lang-card-tray">
                <div className="mt-lang-card">
                    <div className="mt-lang-header">
                        <img src={header} alt="Island Hospital" />
                    </div>

                    {agentCheck.status === 'loading' ? (
                        <h2>Loading... / Memuat...</h2>
                    ) : (
                        <>
                            <h2>Please select a language / Silakan pilih bahasa</h2>

                            <div className="mt-lang-buttons">
                                <button type="button" onClick={() => handleLanguageSelect('en')}>
                                    English
                                </button>
                                <button type="button" onClick={() => handleLanguageSelect('id')}>
                                    Bahasa Indonesia
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default MedicalTravellerFormLanguage;
